if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        
        // Clean up Swiper instance before clearing content
        const modalSlider = this.modalContent.querySelector('#ModalSlider');
        if (modalSlider && modalSlider.swiper) {
          modalSlider.swiper.destroy(true, true);
        }
        
        // Pause and clean up any videos
        const allVideos = this.modalContent.querySelectorAll('video');
        allVideos.forEach(video => {
          video.pause();
          video.currentTime = 0;
        });
        
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // Initialize Swiper for product images in the modal
            this.initializeSwiper();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      initializeSwiper() {
        // Find the modal slider in the quick-add modal content
        const modalSlider = this.modalContent.querySelector('#ModalSlider');
        
        if (modalSlider && typeof Swiper !== 'undefined') {
          // Destroy existing swiper instance if it exists
          if (modalSlider.swiper) {
            modalSlider.swiper.destroy(true, true);
          }

          // Initialize new Swiper instance
          const swiperInstance = new Swiper(modalSlider, {
            navigation: {
              nextEl: modalSlider.querySelector(".swiper-button-next"),
              prevEl: modalSlider.querySelector(".swiper-button-prev"),
            },
            on: {
              slideChange: function() {
                // Pause all videos first
                const allVideos = modalSlider.querySelectorAll('video');
                allVideos.forEach(video => {
                  video.pause();
                  video.currentTime = 0;
                });
                
                // Play video in the new active slide
                const activeSlide = this.slides[this.activeIndex];
                const activeVideo = activeSlide ? activeSlide.querySelector('video') : null;
                if (activeVideo) {
                  activeVideo.play().catch(e => console.log('Video autoplay prevented:', e));
                }
              }
            }
          });

          // Set up video handling for the initial slide
          setTimeout(() => {
            const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
            const activeVideo = activeSlide ? activeSlide.querySelector('video') : null;
            if (activeVideo) {
              activeVideo.currentTime = 0;
              activeVideo.play().catch(e => console.log('Video autoplay prevented:', e));
            }
          }, 100);

          // Make the Swiper instance globally accessible for thumbnail navigation
          modalSlider.swiperInstance = swiperInstance;
        }

        // Initialize thumbnail click handlers and main image functionality
        this.initializeThumbnails();
        
        // Initialize gallery item click handlers
        this.initializeGalleryItems();
      }

      initializeThumbnails() {
        const thumbnails = this.modalContent.querySelectorAll('.thumbnail');
        const mainImageWrapper = this.modalContent.querySelector('.main-image-wrapper');
        const modalSlider = this.modalContent.querySelector('#ModalSlider');

        if (!thumbnails.length) return;

        thumbnails.forEach((thumbnail, index) => {
          // Remove any existing onclick handlers and add new ones
          const newThumbnail = thumbnail.cloneNode(true);
          thumbnail.parentNode.replaceChild(newThumbnail, thumbnail);

          newThumbnail.addEventListener('click', (e) => {
            e.preventDefault();
            const mediaId = newThumbnail.getAttribute('data-media-id');
            const productMedia = Array.from(this.modalContent.querySelectorAll('[data-media-id]'));
            const mediaIndex = productMedia.findIndex(el => el.getAttribute('data-media-id') === mediaId);
            
            if (mediaIndex !== -1 && modalSlider && modalSlider.swiperInstance) {
              // Update swiper to show the clicked thumbnail's slide
              modalSlider.swiperInstance.slideTo(mediaIndex);
              
              // Open the modal if it exists
              const modal = this.modalContent.querySelector('#ImageModal');
              if (modal && modal.classList.contains('hide')) {
                // Trigger modal open
                this.openImageModal(mediaIndex);
              }
            }
          });
        });
      }

      openImageModal(index = 0) {
        const modal = this.modalContent.querySelector('#ImageModal');
        const modalSlider = this.modalContent.querySelector('#ModalSlider');
        
        if (!modal) return;

        modal.classList.remove('hide');
        document.body.classList.add('lock');

        if (modalSlider && modalSlider.swiperInstance) {
          modalSlider.swiperInstance.slideTo(index);
          
          // Handle video playback
          setTimeout(() => {
            const allVideos = modalSlider.querySelectorAll('video');
            allVideos.forEach(video => {
              video.currentTime = 0;
              video.pause();
            });
            
            const activeSlide = modalSlider.swiperInstance.slides[modalSlider.swiperInstance.activeIndex];
            const activeVideo = activeSlide ? activeSlide.querySelector('video') : null;
            if (activeVideo) {
              activeVideo.play().catch(e => console.log('Video autoplay prevented:', e));
            }
          }, 100);
        }

        // Set up close button
        const closeButton = modal.querySelector('.close-modal');
        if (closeButton) {
          closeButton.onclick = () => this.closeImageModal();
        }

        // Set up ESC key handler
        const escHandler = (e) => {
          if (e.key === 'Escape') {
            this.closeImageModal();
            document.removeEventListener('keydown', escHandler);
          }
        };
        document.addEventListener('keydown', escHandler);
      }

      closeImageModal() {
        const modal = this.modalContent.querySelector('#ImageModal');
        const modalSlider = this.modalContent.querySelector('#ModalSlider');
        
        if (!modal) return;

        modal.classList.add('hide');
        document.body.classList.remove('lock');

        // Pause all videos
        if (modalSlider) {
          const allVideos = modalSlider.querySelectorAll('video');
          allVideos.forEach(video => {
            video.pause();
            video.currentTime = 0;
          });
        }
      }

      initializeGalleryItems() {
        // Find all gallery items in the MainImageGallery
        const galleryItems = this.modalContent.querySelectorAll('#MainImageGallery .gallery-item');
        const desktopImage = this.modalContent.querySelector('#DesktopMainImage img');
        
        if (!galleryItems.length && !desktopImage) return;

        // Set up click handlers for mobile gallery items
        galleryItems.forEach((item) => {
          // Remove existing onclick attribute
          item.removeAttribute('onclick');
          
          // Add new click event listener
          item.addEventListener('click', (e) => {
            e.preventDefault();
            const mediaIndex = parseInt(item.getAttribute('data-media-index'), 10);
            this.openImageModal(mediaIndex);
          });
        });

        // Set up click handler for desktop main image
        if (desktopImage) {
          desktopImage.removeAttribute('onclick');
          desktopImage.addEventListener('click', (e) => {
            e.preventDefault();
            this.openImageModal(0);
          });
        }
      }
    }
  );
}
